/**
 * Keys / prefixes that may remain after logout. They hold no session or PII:
 * - `fp_visitor_id` — anonymous device fingerprint
 * - `coachmark-completed:*` — first-visit coachmark completion flags
 * - `capabilities-intro-video-completed:*` — legacy intro-video watched flag (unused for gating)
 *
 * Everything else in localStorage is removed, including `user`, `code_verifier`,
 * `top-query-payload-*`, and `auth-login-session:*`. sessionStorage is cleared entirely.
 */
const ALLOWLISTED_LOCAL_STORAGE_KEYS = new Set(['fp_visitor_id']);
const ALLOWLISTED_LOCAL_STORAGE_PREFIXES = [
  'coachmark-completed:',
  'capabilities-intro-video-completed:',
] as const;

function isAllowlistedLocalStorageKey(key: string): boolean {
  if (ALLOWLISTED_LOCAL_STORAGE_KEYS.has(key)) return true;
  return ALLOWLISTED_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * CASA 6.6.1: wipe user-bound browser storage on logout / stale session.
 * Leaves only the allowlisted keys above; clears all of sessionStorage.
 */
export function clearUserBrowserStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    const { localStorage, sessionStorage } = window;
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || isAllowlistedLocalStorageKey(key)) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
  } catch {
    // Private mode / blocked storage — best effort.
  }
}
